import React, { useEffect, useState } from 'react';
import {
    Alert, Box, CircularProgress, Container, Dialog, DialogContent, DialogTitle,
    IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Tooltip, Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { fetchAllTimeRankings, fetchUserTrophies, medalForPosition } from '../services/history';
import { page, table } from '../theme/commonStyles';

// Bekerkleuren: goud, zilver, brons. Bewust hardcoded — geen thematokens voor edelmetaal.
const CUP_COLORS = { gold: '#FFD700', silver: '#C0C0C0', bronze: '#CD7F32' };
const POSITION_LABELS = { 1: '1e', 2: '2e', 3: '3e' };

/**
 * Kolomkop met een icoon dat bij klik (of hover) toont waar de kolom voor staat.
 * De tooltip is gecontroleerd zodat hij ook op touch-schermen verschijnt.
 */
function HeaderTip({ title, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Tooltip title={title} open={open}>
            <TableCell
                align="center"
                sx={{ width: 52, cursor: 'help' }}
                onClick={() => setOpen((o) => !o)}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
            >
                {children}
            </TableCell>
        </Tooltip>
    );
}

/**
 * Dialog met het seizoensoverzicht van één deelnemer: waar en wanneer de prijzen
 * behaald zijn.
 */
function ParticipantHistoryDialog({ participant, onClose }) {
    const notify = useNotification();
    const [seasons, setSeasons] = useState(null);

    useEffect(() => {
        if (!participant) return undefined;
        let cancelled = false;
        setSeasons(null);
        fetchUserTrophies(participant.uid)
            .then((res) => { if (!cancelled) setSeasons(res.seasons); })
            .catch((err) => {
                console.error('Kon het seizoensoverzicht niet ophalen:', err);
                if (!cancelled) {
                    notify.error('Het seizoensoverzicht kon niet opgehaald worden.');
                    setSeasons([]);
                }
            });
        return () => { cancelled = true; };
    }, [participant, notify]);

    return (
        <Dialog open={Boolean(participant)} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pr: 6 }}>
                {participant?.name}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {seasons === null ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
                ) : seasons.length === 0 ? (
                    <Alert severity="info">Nog geen seizoensresultaten.</Alert>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Seizoen</TableCell>
                                <TableCell align="center">Eindstand</TableCell>
                                <TableCell align="center">Weekprijzen</TableCell>
                                <TableCell align="center">Finale</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {seasons.map((s) => {
                                const cup = medalForPosition(s.position);
                                return (
                                    <TableRow key={s.id}>
                                        <TableCell>{s.seasonName || s.seasonId}</TableCell>
                                        <TableCell align="center">
                                            {cup ? (
                                                <Tooltip title={`${POSITION_LABELS[s.position]} plaats`}>
                                                    <EmojiEventsIcon sx={{ color: CUP_COLORS[cup], verticalAlign: 'middle', fontSize: 20 }} />
                                                </Tooltip>
                                            ) : (s.position ? `${s.position}e` : '—')}
                                        </TableCell>
                                        <TableCell align="center">{s.weekPrizes || '—'}</TableCell>
                                        <TableCell align="center">{s.finalist ? '✔' : '—'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
        </Dialog>
    );
}

/**
 * De Ranglijst aller tijden: per gebruiker geaggregeerd over alle seizoenen, gesorteerd
 * op meeste gouden, dan zilveren, dan bronzen bekers, dan medailles (weekprijzen), dan
 * finaleplekken. Klik op een rij voor het seizoensoverzicht van die deelnemer.
 */
function AllTimeRankings() {
    const { user } = useAuth();
    const notify = useNotification();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchAllTimeRankings()
            .then((data) => { if (!cancelled) setRows(data); })
            .catch((err) => {
                console.error('Kon de ranglijst aller tijden niet ophalen:', err);
                if (!cancelled) notify.error('De ranglijst aller tijden kon niet opgehaald worden.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [notify]);

    if (loading) {
        return (
            <PageLayout>
                <Container sx={page.loadingContainer}><CircularProgress /></Container>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container component="main" maxWidth="md">
                <Box sx={{ mt: 4, mb: 6 }}>
                    <Typography component="h1" variant="h4" gutterBottom sx={{ mb: 3 }}>
                        Ranglijst aller tijden
                    </Typography>

                    {rows.length === 0 ? (
                        <Alert severity="info">
                            Er zijn nog geen resultaten vastgelegd. Een administrator legt deze per
                            seizoen vast via de Instellingen.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 40 }}>#</TableCell>
                                        <TableCell>Speler</TableCell>
                                        <HeaderTip title="Gouden bekers (1e plaats)">
                                            <EmojiEventsIcon sx={{ color: CUP_COLORS.gold, verticalAlign: 'middle' }} />
                                        </HeaderTip>
                                        <HeaderTip title="Zilveren bekers (2e plaats)">
                                            <EmojiEventsIcon sx={{ color: CUP_COLORS.silver, verticalAlign: 'middle' }} />
                                        </HeaderTip>
                                        <HeaderTip title="Bronzen bekers (3e plaats)">
                                            <EmojiEventsIcon sx={{ color: CUP_COLORS.bronze, verticalAlign: 'middle' }} />
                                        </HeaderTip>
                                        <HeaderTip title="Medailles (weekprijzen)">
                                            <MilitaryTechIcon color="primary" sx={{ verticalAlign: 'middle' }} />
                                        </HeaderTip>
                                        <HeaderTip title="Finaleplekken">
                                            <SportsScoreIcon color="action" sx={{ verticalAlign: 'middle' }} />
                                        </HeaderTip>
                                        <HeaderTip title="Deelnames (aantal seizoenen)">
                                            <CalendarMonthIcon color="action" sx={{ verticalAlign: 'middle' }} />
                                        </HeaderTip>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, index) => (
                                        <TableRow
                                            key={row.uid}
                                            hover
                                            onClick={() => setSelected(row)}
                                            sx={{ ...table.clickableCell, ...(row.uid === user?.uid ? table.highlightedRow : {}) }}
                                        >
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell align="center">{row.gold || ''}</TableCell>
                                            <TableCell align="center">{row.silver || ''}</TableCell>
                                            <TableCell align="center">{row.bronze || ''}</TableCell>
                                            <TableCell align="center">{row.medals || ''}</TableCell>
                                            <TableCell align="center">{row.finales || ''}</TableCell>
                                            <TableCell align="center">{row.seasons || ''}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Container>
            <ParticipantHistoryDialog participant={selected} onClose={() => setSelected(null)} />
        </PageLayout>
    );
}

export default AllTimeRankings;
